export const HOUSE_FEE_PCT = 5;

export function percent(value) {
  if (value === null || value === undefined) return '—';
  return `${Math.round(Number(value) * 1000) / 10}%`;
}

export function credits(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
}

export function american(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Math.round(Number(value));
  return n > 0 ? `+${n}` : `${n}`;
}

export function marketTitle(market) {
  if (!market) return '';
  return `${market.team_a || 'Team A'} vs ${market.team_b || 'Team B'}`;
}

export function isScheduledMarket(market) {
  return Boolean(market?.pbleagues_match_id);
}

export function scheduleLabel(market) {
  if (!market) return 'Schedule TBA';

  if (market.starts_at) {
    const d = new Date(market.starts_at);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }

  if (market.date_label && market.start_time_label) {
    return `${market.date_label} • ${market.start_time_label}`;
  }

  if (market.date_label) return market.date_label;
  if (market.start_time_label) return market.start_time_label;

  return 'Schedule TBA';
}

export function marketMetaLine(market) {
  if (!market) return '';

  const parts = [];
  if (market.field) parts.push(market.field);
  if (market.round) parts.push(market.round);
  if (market.bracket_group) parts.push(`Bracket ${market.bracket_group}`);

  return parts.join(' • ');
}

export function teamOdds(market, side) {
  if (!market) return '—';

  if (side === 'team_a') {
    return american(market.current_american_a ?? market.opening_american_a);
  }

  return american(market.current_american_b ?? market.opening_american_b);
}

export function teamProbability(market, side) {
  if (!market) return '—';

  if (side === 'team_a') {
    return percent(
      market.probability_a ??
      market.current_probability_a ??
      market.opening_probability_a
    );
  }

  return percent(
    market.probability_b ??
    market.current_probability_b ??
    market.opening_probability_b
  );
}

export function emptyPoolStats() {
  return {
    positions: 0,
    credits_wagered: 0,
    team_a: {
      tickets: 0,
      credits: 0,
      openTickets: 0,
      openCredits: 0,
    },
    team_b: {
      tickets: 0,
      credits: 0,
      openTickets: 0,
      openCredits: 0,
    },
  };
}

export function getPoolSide(poolStats, side) {
  return poolStats?.[side] || {
    tickets: 0,
    credits: 0,
    openTickets: 0,
    openCredits: 0,
  };
}

export function calculatePoolEstimate(poolStats, side, stake) {
  const stakeAmount = Number(stake || 0);
  const safePool = poolStats || emptyPoolStats();

  const sidePool = Number(getPoolSide(safePool, side).credits || 0);
  const oppositeSide = side === 'team_a' ? 'team_b' : 'team_a';
  const oppositePool = Number(getPoolSide(safePool, oppositeSide).credits || 0);

  const projectedSidePool = sidePool + stakeAmount;
  const projectedTotalPool = Number(safePool.credits_wagered || 0) + stakeAmount;
  const projectedHouseFee = projectedTotalPool * (HOUSE_FEE_PCT / 100);
  const projectedWinnerPool = Math.max(0, projectedTotalPool - projectedHouseFee);

  const hasOpposingAction = oppositePool > 0;

  let estimatedPayout = 0;
  let estimatedProfit = 0;
  let poolShare = 0;
  let returnMultiple = 0;

  if (hasOpposingAction && projectedSidePool > 0 && stakeAmount > 0) {
    poolShare = stakeAmount / projectedSidePool;
    estimatedPayout = poolShare * projectedWinnerPool;
    estimatedProfit = estimatedPayout - stakeAmount;
    returnMultiple = estimatedPayout / stakeAmount;
  }

  return {
    stakeAmount,
    sidePool,
    oppositePool,
    projectedSidePool,
    projectedTotalPool,
    projectedHouseFee,
    projectedWinnerPool,
    hasOpposingAction,
    estimatedPayout,
    estimatedProfit,
    poolShare,
    returnMultiple,
  };
}

export function poolHealth(poolStats) {
  const safePool = poolStats || emptyPoolStats();

  const a = Number(safePool.team_a?.credits || 0);
  const b = Number(safePool.team_b?.credits || 0);
  const total = a + b;

  if (total <= 0) {
    return {
      className: 'danger',
      label: 'No Action',
      detail: 'No credits are in this market yet.',
    };
  }

  if (a <= 0 || b <= 0) {
    return {
      className: 'danger',
      label: 'One-Sided Pool',
      detail: 'No opposing action yet. Estimated payout is unavailable.',
    };
  }

  if (total < 1000) {
    return {
      className: 'warning',
      label: 'Thin Pool',
      detail: 'Payout estimates may move quickly as more credits enter.',
    };
  }

  return {
    className: 'good',
    label: 'Active Pool',
    detail: 'Both sides have action.',
  };
}